import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { MarketplaceSelection } from './MarketplaceSelection';
import { ResourceConfiguration } from './ResourceConfiguration';
import { initializeApiClient } from 'utils/apiClient';
import { useTenantContext } from 'contexts/TenantContext';
import { useResourceData } from '../ResourceDetailModal/hooks/useResourceData';
import { useResourceForm } from '../ResourceDetailModal/hooks/useResourceForm';
import { useResourceValidation } from '../ResourceDetailModal/hooks/useResourceValidation';
import { isPartnerTenant } from 'utils/tenantUtils';
import { useAuth } from 'contexts/AuthContext';
import '../ResourceDetailModal/styles/MarketplacePicker.css';
import '../ResourceDetailModal/styles/MarketplaceWizard.css';

/**
 * MarketplaceModule - Single source of truth for marketplace functionality
 * 
 * Modes:
 * - "standalone": Full marketplace page with complete create flow
 * - "embedded": Embedded in ResourceDetailModal, uses parent callbacks
 */
export const MarketplaceModule = ({
  mode = 'embedded', // 'standalone' or 'embedded'
  headerRightEl = null, // when set (standalone page), steps are portaled into header right
  // Embedded mode props (from ResourceDetailModal)
  formData: externalFormData,
  handleInputChange: externalHandleInputChange,
  handleSubmit: externalHandleSubmit,
  handleClose: externalHandleClose,
  loading: externalLoading,
  tenants: externalTenants,
  loadingTenants: externalLoadingTenants,
  dropdownOpen: externalDropdownOpen,
  setDropdownOpen: externalSetDropdownOpen,
  setFormData: externalSetFormData,
  setIsSelectedTenantPartner: externalSetIsSelectedTenantPartner,
  setIsDevTenant: externalSetIsDevTenant,
  loadCostPools: externalLoadCostPools,
  loadPrices: externalLoadPrices,
  handleRetryTenants: externalHandleRetryTenants,
  costPools: externalCostPools,
  loadingCostPools: externalLoadingCostPools,
  isDevTenant: externalIsDevTenant,
  isSelectedTenantPartner: externalIsSelectedTenantPartner,
  isDevResource: externalIsDevResource,
  isCurrentProductFree: externalIsCurrentProductFree,
  isCostPoolRequired: externalIsCostPoolRequired,
  setIsDevResource: externalSetIsDevResource,
  products: externalProducts,
  loadingProducts: externalLoadingProducts,
  handleProductSelect: externalHandleProductSelect,
  prices: externalPrices,
  loadingPrices: externalLoadingPrices,
  handleSubscriptionSelect: externalHandleSubscriptionSelect,
  isFormValid: externalIsFormValid,
  setStepTitle: externalSetStepTitle,
  setStepDescription: externalSetStepDescription,
  setCurrentWizardStep: externalSetCurrentWizardStep
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();
  useTenantContext();
  const { isAuthenticated } = useAuth();
  
  // Internal state for standalone mode
  const [internalFormData, setInternalFormData] = useState({
    name: '',
    description: '',
    type: 'bot',
    tenant_id: '',
    cost_pool_id: '',
    configuration: {}
  });
  const [internalIsDevTenant, setInternalIsDevTenant] = useState(false);
  const [internalIsSelectedTenantPartner, setInternalIsSelectedTenantPartner] = useState(false);
  const [internalIsDevResource, setInternalIsDevResource] = useState(false);
  const [internalDropdownOpen, setInternalDropdownOpen] = useState(false);
  // Initialize step from URL so SDK deep link (?stage=configure) is not overwritten to selection on first paint
  const [currentStep, setCurrentStep] = useState(() => {
    if (typeof window === 'undefined') return 1;
    const params = new URLSearchParams(window.location.search);
    const s = params.get('stage');
    if (s === 'configure' || s === '2') return 2;
    if (s === 'selection' || s === '1') return 1;
    return 1;
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [internalLoading, setInternalLoading] = useState(false);
  
  // Refs to prevent infinite loops
  const dataLoadedRef = useRef(false);
  const tenantAutoSelectedRef = useRef(false);
  const urlParamsReadRef = useRef(false);
  const productFromUrlRef = useRef(null);
  const lastPricesLoadRef = useRef(null); // { pid, tid } to avoid re-calling loadPrices in a loop
  
  // Use external props in embedded mode, internal state in standalone mode
  const isEmbedded = mode === 'embedded';
  const formData = isEmbedded ? externalFormData : internalFormData;
  const setFormData = isEmbedded ? externalSetFormData : setInternalFormData;
  const isDevTenant = isEmbedded ? externalIsDevTenant : internalIsDevTenant;
  const setIsDevTenant = isEmbedded ? externalSetIsDevTenant : setInternalIsDevTenant;
  const isSelectedTenantPartner = isEmbedded ? externalIsSelectedTenantPartner : internalIsSelectedTenantPartner;
  const setIsSelectedTenantPartner = isEmbedded ? externalSetIsSelectedTenantPartner : setInternalIsSelectedTenantPartner;
  const isDevResource = isEmbedded ? externalIsDevResource : internalIsDevResource;
  const setIsDevResource = isEmbedded ? externalSetIsDevResource : setInternalIsDevResource;
  const dropdownOpen = isEmbedded ? externalDropdownOpen : internalDropdownOpen;
  const setDropdownOpen = isEmbedded ? externalSetDropdownOpen : setInternalDropdownOpen;
  const loading = isEmbedded ? externalLoading : internalLoading;
  
  // Data hooks - only used in standalone mode
  const dataHook = useResourceData({
    resourceId: null,
    isOpen: !isEmbedded,
    mode: 'create',
    formData: internalFormData,
    tenants: [],
    isSelectedTenantPartner: internalIsSelectedTenantPartner,
    setIsDevTenant: setInternalIsDevTenant,
    setFormData: setInternalFormData,
    setNextPlanLookupKey: () => {}
  });
  
  const {
    tenants: dataHookTenants,
    costPools: dataHookCostPools,
    products: dataHookProducts,
    prices: dataHookPrices,
    loadingTenants: dataHookLoadingTenants,
    loadingCostPools: dataHookLoadingCostPools,
    loadingProducts: dataHookLoadingProducts,
    loadingPrices: dataHookLoadingPrices,
    loadTenants: dataHookLoadTenants,
    loadCostPools: dataHookLoadCostPools,
    loadProducts: dataHookLoadProducts,
    loadPrices: dataHookLoadPrices,
    handleRetryTenants: dataHookHandleRetryTenants
  } = dataHook;
  
  // Use external data in embedded mode, internal data in standalone mode
  const tenants = isEmbedded ? externalTenants : dataHookTenants;
  const loadingTenants = isEmbedded ? externalLoadingTenants : dataHookLoadingTenants;
  const costPools = isEmbedded ? externalCostPools : dataHookCostPools;
  const loadingCostPools = isEmbedded ? externalLoadingCostPools : dataHookLoadingCostPools;
  const products = isEmbedded ? externalProducts : dataHookProducts;
  const loadingProducts = isEmbedded ? externalLoadingProducts : dataHookLoadingProducts;
  const prices = isEmbedded ? externalPrices : dataHookPrices;
  const loadingPrices = isEmbedded ? externalLoadingPrices : dataHookLoadingPrices;
  const loadCostPools = isEmbedded ? externalLoadCostPools : dataHookLoadCostPools;
  const loadPrices = isEmbedded ? externalLoadPrices : dataHookLoadPrices;
  // eslint-disable-next-line no-unused-vars
  const handleRetryTenants = isEmbedded ? externalHandleRetryTenants : dataHookHandleRetryTenants;
  
  // Validation hook
  const {
    isCurrentProductFree,
    isCostPoolRequired,
    isFormValid: validationIsFormValid
  } = useResourceValidation({
    formData,
    mode: 'create',
    isDevTenant,
    isSelectedTenantPartner,
    isDevResource,
    products,
    prices
  });
  
  // Ensure isFormValid is always a function
  const isFormValid = isEmbedded 
    ? (typeof externalIsFormValid === 'function' ? externalIsFormValid : () => true)
    : (typeof validationIsFormValid === 'function' ? validationIsFormValid : () => true);
  const isCurrentProductFreeValue = isEmbedded ? externalIsCurrentProductFree : isCurrentProductFree;
  const isCostPoolRequiredValue = isEmbedded ? externalIsCostPoolRequired : isCostPoolRequired;
  
  // Form hook - always called (React hooks rules), but only used in standalone mode
  const formHook = useResourceForm({
    mode: 'create',
    resourceId: null,
    initialMode: 'create',
    isDevTenant: isEmbedded ? externalIsDevTenant : internalIsDevTenant,
    isSelectedTenantPartner: isEmbedded ? externalIsSelectedTenantPartner : internalIsSelectedTenantPartner,
    isDevResource: isEmbedded ? externalIsDevResource : internalIsDevResource,
    setIsDevTenant: isEmbedded ? () => {} : setInternalIsDevTenant,
    setIsSelectedTenantPartner: isEmbedded ? () => {} : setInternalIsSelectedTenantPartner,
    setIsDevResource: isEmbedded ? () => {} : setInternalIsDevResource,
    tenants: isEmbedded ? externalTenants : dataHookTenants,
    products: isEmbedded ? externalProducts : dataHookProducts,
    loadCostPools: isEmbedded ? () => {} : dataHookLoadCostPools,
    loadPrices: isEmbedded ? () => {} : dataHookLoadPrices,
    onClose: () => {},
    onSuccess: () => {
      // Navigate to resources page on success (only used in standalone mode)
      if (!isEmbedded) {
        navigate('/?tab=resources');
      }
    },
    setError: () => {},
    setLoading: isEmbedded ? () => {} : setInternalLoading,
    isCurrentProductFreeFn: isCurrentProductFree,
    isCostPoolRequiredFn: isCostPoolRequired,
    formData: isEmbedded ? externalFormData : internalFormData,
    setFormData: isEmbedded ? () => {} : setInternalFormData,
    navigate: isEmbedded ? () => {} : navigate
  });
  
  const handleInputChange = isEmbedded ? externalHandleInputChange : (formHook?.handleInputChange || (() => {}));
  // Ensure handleSubmit is properly available in standalone mode
  const handleSubmit = isEmbedded 
    ? externalHandleSubmit 
    : (formHook?.handleSubmit || (() => {
        console.error('handleSubmit not available in formHook');
        return Promise.resolve();
      }));
  const handleClose = isEmbedded ? externalHandleClose : (() => navigate('/'));
  
  // Handle subscription/price selection - update formData in standalone mode
  const handleSubscriptionSelect = isEmbedded 
    ? externalHandleSubscriptionSelect 
    : ((priceIdOrPrice) => {
        // Call the form hook handler if available
        if (formHook?.handleSubscriptionSelect) {
          formHook.handleSubscriptionSelect(priceIdOrPrice);
        }
        
        // Also update formData directly in standalone mode to ensure price_lookup_key is set
        const priceId = typeof priceIdOrPrice === 'string' 
          ? priceIdOrPrice 
          : (priceIdOrPrice?.id || priceIdOrPrice?.lookup_key);
        
        if (priceId) {
          setFormData(prev => ({
            ...prev,
            configuration: {
              ...prev.configuration,
              price_lookup_key: priceId
            }
          }));
          
          // Also update selectedPrice state
          if (prices.length > 0) {
            const price = prices.find(p => 
              p.id === priceId || 
              p.lookup_key === priceId
            );
            if (price) {
              setSelectedPrice(price);
            }
          }
        }
      });
  
  // Expose current step to parent for header animation (embedded mode)
  useEffect(() => {
    if (isEmbedded && externalSetCurrentWizardStep) {
      externalSetCurrentWizardStep(currentStep);
    }
  }, [currentStep, isEmbedded, externalSetCurrentWizardStep]);
  
  // Read URL params on mount to support deep linking (standalone mode only)
  // URL pattern: /marketplace?product=prod_xxx&stage=configure&price=price_xxx
  useEffect(() => {
    if (isEmbedded || urlParamsReadRef.current) return;
    
    const urlParams = new URLSearchParams(location.search);
    const productParam = urlParams.get('product');
    const stageParam = urlParams.get('stage');
    const priceParam = urlParams.get('price');
    const priceLookupFromUrl = (priceParam && typeof priceParam === 'string' && priceParam.startsWith('price_'))
      ? priceParam
      : undefined;
    
    if (productParam) {
      productFromUrlRef.current = productParam;
      // Set product_id in formData immediately so loadPrices runs as soon as tenant is set (don't wait for products API)
      setFormData(prev => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          product_id: productParam,
          ...(priceLookupFromUrl ? { price_lookup_key: priceLookupFromUrl } : {})
        }
      }));
    } else if (priceLookupFromUrl) {
      setFormData(prev => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          price_lookup_key: priceLookupFromUrl
        }
      }));
    }
    
    if (stageParam) {
      if (stageParam === 'configure' || stageParam === '2') {
        setCurrentStep(2);
      } else if (stageParam === 'selection' || stageParam === '1') {
        setCurrentStep(1);
      }
    }
    
    urlParamsReadRef.current = true;
  }, [isEmbedded, location.search, setFormData]);
  
  // Select product from URL param when products are loaded (standalone mode only)
  // If product is not in the list (e.g. filtered by metadata), still set product_id so loadPrices runs and price UI shows
  // Always preserve price from URL when it's a Stripe price ID (price_xxx) so SDK→marketplace→checkout uses correct price
  useEffect(() => {
    if (isEmbedded || !productFromUrlRef.current) return;
    
    const productId = productFromUrlRef.current;
    const partnerParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('partner') : null;
    const priceParam = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('price') : null;
    const urlPriceLookup = (priceParam && typeof priceParam === 'string' && priceParam.startsWith('price_')) ? priceParam : null;
    
    const product = products.length > 0
      ? (products.find(p => p.id === productId) ||
        (partnerParam && products.find(p =>
          p.metadata?.resale_resource_id === partnerParam && (p.metadata?.stripe_product_id === productId || p.id === productId)
        )))
      : null;
    
    if (product && !selectedProduct) {
      setSelectedProduct(product);
      const resourceType = product.metadata?.resource_type || (product.metadata?.type || 'bot');
      const isResale = product.metadata?.resource_type === 'resale' || !!product.metadata?.from_resale_resource;
      const resaleResourceId = product.metadata?.resale_resource_id || partnerParam;
      setFormData(prev => ({
        ...prev,
        type: resourceType,
        configuration: {
          ...prev.configuration,
          product_id: product.id,
          ...(isResale ? { is_resale_product: true } : {}),
          ...(resaleResourceId ? { resale_resource_id: resaleResourceId } : {}),
          ...(urlPriceLookup ? { price_lookup_key: urlPriceLookup } : {})
        }
      }));
      productFromUrlRef.current = null;
      return;
    }
    
    // Product from URL not in list – still set product_id and resale_resource_id so backend can resolve
    if (products.length > 0 && !selectedProduct && formData.configuration?.product_id !== productId) {
      const hasPartnerParam = !!partnerParam;
      setFormData(prev => ({
        ...prev,
        type: prev.type || 'bot',
        configuration: {
          ...prev.configuration,
          product_id: productId,
          ...(hasPartnerParam ? { is_resale_product: true } : {}),
          ...(partnerParam ? { resale_resource_id: partnerParam } : {}),
          ...(urlPriceLookup ? { price_lookup_key: urlPriceLookup } : {})
        }
      }));
      productFromUrlRef.current = null;
    }
  }, [isEmbedded, products, selectedProduct, setFormData, formData.configuration?.product_id]);
  
  // Update URL when step changes (standalone mode only)
  // This enables back/forward browser navigation and deep linking
  useEffect(() => {
    if (isEmbedded || !urlParamsReadRef.current) return;
    
    const urlParams = new URLSearchParams(location.search);
    const currentStageParam = urlParams.get('stage');
    const newStageParam = currentStep === 1 ? 'selection' : 'configure';
    
    // Only update if stage changed (avoid unnecessary history entries)
    if (currentStageParam !== newStageParam) {
      urlParams.set('stage', newStageParam);
      
      // Preserve product param if selected
      if (selectedProduct?.id) {
        urlParams.set('product', selectedProduct.id);
      }
      
      // Preserve partner param if present
      const partnerParam = new URLSearchParams(location.search).get('partner');
      if (partnerParam) {
        urlParams.set('partner', partnerParam);
      }
      // Preserve selection step params (view and provider modal)
      const viewParam = new URLSearchParams(location.search).get('view');
      if (viewParam) urlParams.set('view', viewParam);
      const providerParam = new URLSearchParams(location.search).get('provider');
      if (providerParam) urlParams.set('provider', providerParam);
      
      // Use replaceState to avoid cluttering browser history
      const newUrl = `${location.pathname}?${urlParams.toString()}`;
      window.history.replaceState({}, '', newUrl);
    }
  }, [currentStep, isEmbedded, location.pathname, location.search, selectedProduct?.id]);
  
  // Update step title/description when step changes
  // Note: Translations are handled in MarketplaceHeader component
  useEffect(() => {
    if (currentStep === 1) {
      if (externalSetStepTitle) externalSetStepTitle('Marketplace');
      if (externalSetStepDescription) externalSetStepDescription('SaaS and Services at Your Fingertips');
    } else if (currentStep === 2) {
      if (externalSetStepTitle) externalSetStepTitle('Configuration');
      if (externalSetStepDescription) externalSetStepDescription('Tailor Your Resource to Your Needs');
    }
  }, [currentStep, externalSetStepTitle, externalSetStepDescription]);
  
  // Initialize selected product from formData
  useEffect(() => {
    if (formData.configuration?.product_id && products.length > 0) {
      const product = products.find(p => p.id === formData.configuration.product_id);
      if (product) {
        setSelectedProduct(product);
      }
    }
  }, [formData.configuration?.product_id, products]);
  
  // Initialize selected price from formData
  useEffect(() => {
    if (formData.configuration?.price_lookup_key && prices.length > 0) {
      const price = prices.find(p => 
        p.id === formData.configuration.price_lookup_key || 
        p.lookup_key === formData.configuration.price_lookup_key
      );
      if (price) {
        setSelectedPrice(price);
      }
    }
  }, [formData.configuration?.price_lookup_key, prices]);

  // When landing on config step with product+tenant (e.g. from resale SDK "Go to Marketplace"), load prices once per (pid, tid)
  // Reset ref when leaving step 2 so returning to config step refetches
  useEffect(() => {
    if (currentStep !== 2) {
      lastPricesLoadRef.current = null;
      return;
    }
    if (isEmbedded || !loadPrices) return;
    const tid = formData.tenant_id;
    const pid = formData.configuration?.product_id || (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('product'));
    if (!tid || !pid) return;
    const key = `${pid}:${tid}`;
    if (lastPricesLoadRef.current === key) return;
    lastPricesLoadRef.current = key;
    loadPrices(pid, tid);
  }, [isEmbedded, currentStep, formData.tenant_id, formData.configuration?.product_id, loadPrices]);

  // Load data in standalone mode (only once, and only if authenticated)
  useEffect(() => {
    if (!isEmbedded && isAuthenticated && instance && instance.getAllAccounts().length > 0 && !dataLoadedRef.current) {
      dataLoadedRef.current = true;
      (async () => {
        await initializeApiClient(instance);
        dataHookLoadTenants();
        dataHookLoadProducts();
      })();
    } else if (!isEmbedded && !isAuthenticated && !dataLoadedRef.current) {
      // Mark as loaded to prevent retries when not authenticated
      // Products will be empty - UI will show login prompt
      dataLoadedRef.current = true;
    }
  }, [isEmbedded, isAuthenticated, instance]); // Removed function dependencies to prevent loops
  
  // Auto-select first tenant when tenants are loaded in standalone mode (only once)
  useEffect(() => {
    if (!isEmbedded && tenants.length > 0 && !formData.tenant_id && !loadingTenants && !tenantAutoSelectedRef.current) {
      const bestTenant = tenants.find(tenant => !tenant.is_dev_tenant) || tenants[0];
      if (bestTenant) {
        tenantAutoSelectedRef.current = true;
        const isPartner = isPartnerTenant(bestTenant);
        const isDev = bestTenant.is_dev_tenant || false;
        
        setFormData(prev => ({
          ...prev,
          tenant_id: bestTenant.id
        }));
        
        setIsSelectedTenantPartner(isPartner);
        setIsDevTenant(isDev);
      }
    }
  }, [isEmbedded, tenants.length, formData.tenant_id, loadingTenants]); // Removed function dependencies
  
  // Handle product selection/deselection in Step 1
  const handleProductSelectStep1 = (product) => {
    // If clicking the same product, deselect it
    if (selectedProduct && selectedProduct.id === product.id) {
      setSelectedProduct(null);
      setSelectedPrice(null);
      if (externalHandleProductSelect) {
        externalHandleProductSelect(null);
      }
      // Clear product from formData in standalone mode
      if (!isEmbedded) {
        setFormData(prev => ({
          ...prev,
          configuration: {
            ...prev.configuration,
            product_id: undefined,
            price_lookup_key: undefined
          }
        }));
      }
      return; // Don't auto-navigate on deselection
    }
    
    // Select new product
    setSelectedProduct(product);
    
    // Determine resource type from product metadata
    const resourceType = product.metadata?.resource_type || 
                        (product.metadata?.type || 'bot');
    
    // Update formData with product and resource type
    if (externalHandleProductSelect) {
      externalHandleProductSelect(product);
    }
    
    // Set resource type and product_id in formData (critical for standalone mode). Include resale_resource_id from catalog so payload matches URL flow.
    const isResale = product.metadata?.resource_type === 'resale' || !!product.metadata?.from_resale_resource;
    const resaleResourceId = isResale ? (product.metadata?.resale_resource_id || null) : null;
    setFormData(prev => ({
      ...prev,
      type: resourceType,
      configuration: {
        ...prev.configuration,
        product_id: product.id,
        ...(isResale ? { is_resale_product: true } : { is_resale_product: undefined }),
        ...(resaleResourceId ? { resale_resource_id: resaleResourceId } : {})
      }
    }));
    
    // Prices will be loaded in the configuration step when tenant is selected
    // Do NOT load prices here - they require tenant context for proper filtering
    
    // Advance to Configuration step immediately when selecting from modal
    if (isAuthenticated && currentStep === 1) {
      setCurrentStep(2);
    }
  };
  
  // Handle Next button on marketplace step
  const handleMarketplaceNext = () => {
    if (selectedProduct) {
      setCurrentStep(2); // Go to Configuration
    }
  };
  
  // Go back to Marketplace (clear provider from URL so mobile provider drill-down does not re-open)
  const handleBackToMarketplace = () => {
    setCurrentStep(1);
    if (isEmbedded) return;
    const merged = new URLSearchParams(
      typeof window !== 'undefined' ? window.location.search : location.search
    );
    merged.delete('provider');
    merged.set('stage', 'selection');
    const qs = merged.toString();
    const newUrl = qs ? `${location.pathname}?${qs}` : location.pathname;
    if (typeof window !== 'undefined') {
      window.history.replaceState({}, '', newUrl);
    }
  };
  
  // Handle final submission - must be defined before handleContinueToReview
  const handleFinalSubmit = useCallback((e) => {
    if (e) {
      e.preventDefault();
    }
    if (loading) {
      console.log('Submission blocked: already loading');
      return;
    }
    if (!handleSubmit || typeof handleSubmit !== 'function') {
      console.error('handleSubmit is not available or not a function:', handleSubmit);
      return;
    }
    // Double-check validation before submitting
    const isValid = isFormValid();
    console.log('Form validation before submit:', isValid);
    if (isValid) {
      const syntheticEvent = {
        preventDefault: () => {},
        target: { checkValidity: () => true }
      };
      console.log('Calling handleSubmit');
      handleSubmit(syntheticEvent).catch((error) => {
        console.error('Error submitting form:', error);
      });
    } else {
      console.warn('Form validation failed, cannot submit');
    }
  }, [loading, isFormValid, handleSubmit]);
  
  // Handle final submission from Configuration
  const handleContinueToReview = useCallback(() => {
    if (loading) {
      return;
    }
    const hasProduct = selectedProduct || formData.configuration?.product_id;
    if (!hasProduct) {
      console.warn('Cannot submit: no product selected');
      return;
    }
    // Check form validity with detailed logging
    console.log('Form validation check:', {
      formData,
      selectedProduct: selectedProduct?.id,
      selectedPrice: selectedPrice?.id,
      isFormValid: typeof isFormValid,
      isFormValidResult: isFormValid()
    });
    const isValid = isFormValid();
    if (isValid) {
      handleFinalSubmit();
    } else {
      console.warn('Cannot submit: form validation failed', {
        name: formData.name,
        tenant_id: formData.tenant_id,
        cost_pool_id: formData.cost_pool_id,
        product_id: formData.configuration?.product_id,
        price_lookup_key: formData.configuration?.price_lookup_key,
        selectedPrice: selectedPrice?.id
      });
    }
  }, [selectedProduct, isFormValid, loading, handleFinalSubmit, formData, selectedPrice]);
  
  // Step 2: Configuration (includes price selection)
  if (currentStep === 2) {
    return (
      <form onSubmit={(e) => { e.preventDefault(); handleContinueToReview(); }}>
        <ResourceConfiguration
          formData={formData}
          handleInputChange={handleInputChange}
          selectedProduct={selectedProduct}
          selectedPrice={selectedPrice}
          prices={prices}
          loadingPrices={loadingPrices}
          handleSubscriptionSelect={handleSubscriptionSelect}
          loadPrices={loadPrices}
          tenants={tenants}
          loadingTenants={loadingTenants}
          dropdownOpen={dropdownOpen}
          setDropdownOpen={setDropdownOpen}
          setFormData={setFormData}
          setIsSelectedTenantPartner={setIsSelectedTenantPartner}
          setIsDevTenant={setIsDevTenant}
          loadCostPools={loadCostPools}
          costPools={costPools}
          loadingCostPools={loadingCostPools}
          isDevTenant={isDevTenant}
          isSelectedTenantPartner={isSelectedTenantPartner}
          isDevResource={isDevResource}
          isCurrentProductFree={isCurrentProductFreeValue}
          isCostPoolRequired={isCostPoolRequiredValue}
          setIsDevResource={setIsDevResource}
          mode="create"
          isFormValid={isFormValid}
          onBack={handleBackToMarketplace}
          onSubmit={handleContinueToReview}
          setStepTitle={externalSetStepTitle}
          setStepDescription={externalSetStepDescription}
          setCurrentWizardStep={externalSetCurrentWizardStep}
          onClose={handleClose}
          mainWizardStep={currentStep}
          loading={loading}
        />
      </form>
    );
  }
  
  // Step 1: Marketplace Selection
  return (
    <MarketplaceSelection
      products={products}
      loadingProducts={loadingProducts}
      selectedProductId={selectedProduct?.id}
      onProductSelect={isAuthenticated ? handleProductSelectStep1 : () => {}}
      loading={loading}
      selectedProduct={selectedProduct}
      selectedPrice={selectedPrice}
      showPriceStep={false}
      onNext={isAuthenticated ? handleMarketplaceNext : null}
      isAuthenticated={isAuthenticated}
      headerRightEl={headerRightEl}
      isPublicPortal={true}
    />
  );
};

